/**
 * Supabase Quotes API Service
 *
 * Handles all quote operations.
 */

import { supabase } from './client';
import type { Quote, QuoteInsert, QuoteUpdate, Json } from './database.types';

// Quote status type
export type QuoteStatus = 'draft' | 'sent' | 'accepted' | 'rejected' | 'expired';

// Additional item type
export interface QuoteAdditionalItem {
  id: string;
  description: string;
  quantity: number;
  unitPrice: number;
  total: number;
}

// Frontend Quote type (camelCase)
export interface QuoteData {
  id: string;
  createdAt: string;
  updatedAt: string;
  leadId: string | null;
  quoteNumber: string;
  quoteDate: string;
  validUntil: string;
  // Client Info
  clientName: string;
  clientContact: string | null;
  clientAddress: string | null;
  clientCity: string | null;
  clientState: string;
  clientZip: string | null;
  // Project Info
  projectName: string | null;
  projectAddress: string | null;
  projectCity: string | null;
  projectState: string;
  projectZip: string | null;
  // Pricing
  linearFootage: number;
  pricePerFoot: number;
  controllerPrice: number;
  powerSupplyPrice: number | null;
  includePowerSupply: boolean;
  additionalItems: QuoteAdditionalItem[];
  // Totals
  subtotal: number;
  taxRate: number;
  taxAmount: number;
  totalAmount: number;
  // Deposit
  depositPercent: number;
  depositAmount: number;
  // Status
  status: QuoteStatus;
  userId: string | null;
}

// Map database quote to frontend format
const mapQuoteToData = (quote: Quote): QuoteData => ({
  id: quote.id,
  createdAt: quote.created_at,
  updatedAt: quote.updated_at,
  leadId: quote.lead_id,
  quoteNumber: quote.quote_number,
  quoteDate: quote.quote_date,
  validUntil: quote.valid_until,
  clientName: quote.client_name,
  clientContact: quote.client_contact,
  clientAddress: quote.client_address,
  clientCity: quote.client_city,
  clientState: quote.client_state,
  clientZip: quote.client_zip,
  projectName: quote.project_name,
  projectAddress: quote.project_address,
  projectCity: quote.project_city,
  projectState: quote.project_state,
  projectZip: quote.project_zip,
  linearFootage: quote.linear_footage,
  pricePerFoot: quote.price_per_foot,
  controllerPrice: quote.controller_price,
  powerSupplyPrice: quote.power_supply_price,
  includePowerSupply: quote.include_power_supply,
  additionalItems: (quote.additional_items as unknown as QuoteAdditionalItem[]) || [],
  subtotal: quote.subtotal,
  taxRate: quote.tax_rate,
  taxAmount: quote.tax_amount,
  totalAmount: quote.total_amount,
  depositPercent: quote.deposit_percent,
  depositAmount: quote.deposit_amount,
  status: quote.status,
  userId: quote.user_id,
});

// Map frontend quote to database format
const mapDataToQuote = (data: Partial<QuoteData>): Partial<QuoteInsert> => {
  const mapped: Partial<QuoteInsert> = {};

  if (data.leadId !== undefined) mapped.lead_id = data.leadId;
  if (data.quoteNumber !== undefined) mapped.quote_number = data.quoteNumber;
  if (data.quoteDate !== undefined) mapped.quote_date = data.quoteDate;
  if (data.validUntil !== undefined) mapped.valid_until = data.validUntil;
  if (data.clientName !== undefined) mapped.client_name = data.clientName;
  if (data.clientContact !== undefined) mapped.client_contact = data.clientContact;
  if (data.clientAddress !== undefined) mapped.client_address = data.clientAddress;
  if (data.clientCity !== undefined) mapped.client_city = data.clientCity;
  if (data.clientState !== undefined) mapped.client_state = data.clientState;
  if (data.clientZip !== undefined) mapped.client_zip = data.clientZip;
  if (data.projectName !== undefined) mapped.project_name = data.projectName;
  if (data.projectAddress !== undefined) mapped.project_address = data.projectAddress;
  if (data.projectCity !== undefined) mapped.project_city = data.projectCity;
  if (data.projectState !== undefined) mapped.project_state = data.projectState;
  if (data.projectZip !== undefined) mapped.project_zip = data.projectZip;
  if (data.linearFootage !== undefined) mapped.linear_footage = data.linearFootage;
  if (data.pricePerFoot !== undefined) mapped.price_per_foot = data.pricePerFoot;
  if (data.controllerPrice !== undefined) mapped.controller_price = data.controllerPrice;
  if (data.powerSupplyPrice !== undefined) mapped.power_supply_price = data.powerSupplyPrice;
  if (data.includePowerSupply !== undefined) mapped.include_power_supply = data.includePowerSupply;
  if (data.additionalItems !== undefined) mapped.additional_items = data.additionalItems as unknown as Json;
  if (data.subtotal !== undefined) mapped.subtotal = data.subtotal;
  if (data.taxRate !== undefined) mapped.tax_rate = data.taxRate;
  if (data.taxAmount !== undefined) mapped.tax_amount = data.taxAmount;
  if (data.totalAmount !== undefined) mapped.total_amount = data.totalAmount;
  if (data.depositPercent !== undefined) mapped.deposit_percent = data.depositPercent;
  if (data.depositAmount !== undefined) mapped.deposit_amount = data.depositAmount;
  if (data.status !== undefined) mapped.status = data.status;
  if (data.userId !== undefined) mapped.user_id = data.userId;

  return mapped;
};

export interface GetQuotesParams {
  leadId?: string;
  status?: QuoteStatus;
  page?: number;
  pageSize?: number;
}

export interface GetQuotesResponse {
  data: QuoteData[];
  count: number;
}

class QuotesApi {
  /**
   * Get all quotes with optional filters
   */
  async getAll(params: GetQuotesParams = {}): Promise<GetQuotesResponse> {
    const pageSize = params.pageSize || 25;
    const page = params.page || 1;
    const offset = (page - 1) * pageSize;

    let query = supabase
      .from('quotes')
      .select('*', { count: 'exact' });

    // Apply filters
    if (params.leadId) {
      query = query.eq('lead_id', params.leadId);
    }

    if (params.status) {
      query = query.eq('status', params.status);
    }

    // Apply sorting (newest first)
    query = query.order('created_at', { ascending: false });

    // Apply pagination
    query = query.range(offset, offset + pageSize - 1);

    const { data, error, count } = await query;

    if (error) {
      // console.error('Failed to fetch quotes:', error);
      throw error;
    }

    return {
      data: (data || []).map(mapQuoteToData),
      count: count || 0,
    };
  }

  /**
   * Get quotes for a specific lead
   */
  async getByLead(leadId: string): Promise<QuoteData[]> {
    const { data, error } = await supabase
      .from('quotes')
      .select('*')
      .eq('lead_id', leadId)
      .order('created_at', { ascending: false });

    if (error) {
      // console.error('Failed to fetch quotes by lead:', error);
      throw error;
    }

    return (data || []).map(mapQuoteToData);
  }

  /**
   * Get a single quote by ID
   */
  async getById(id: string): Promise<QuoteData | null> {
    const { data, error } = await supabase
      .from('quotes')
      .select('*')
      .eq('id', id)
      .single();

    if (error) {
      if (error.code === 'PGRST116') {
        return null;
      }
      // console.error('Failed to fetch quote:', error);
      throw error;
    }

    return data ? mapQuoteToData(data) : null;
  }

  /**
   * Get quote by quote number
   */
  async getByQuoteNumber(quoteNumber: string): Promise<QuoteData | null> {
    const { data, error } = await supabase
      .from('quotes')
      .select('*')
      .eq('quote_number', quoteNumber)
      .single();

    if (error) {
      if (error.code === 'PGRST116') {
        return null;
      }
      // console.error('Failed to fetch quote by number:', error);
      throw error;
    }

    return data ? mapQuoteToData(data) : null;
  }

  /**
   * Create a new quote
   */
  async create(quoteData: Omit<QuoteData, 'id' | 'createdAt' | 'updatedAt'>): Promise<QuoteData> {
    // console.log('quotesApi.create called with:', {
    //   quoteNumber: quoteData.quoteNumber,
    //   leadId: quoteData.leadId,
    // });

    const dbData = mapDataToQuote(quoteData) as QuoteInsert;

    const { data, error } = await supabase
      .from('quotes')
      .insert(dbData as any)
      .select()
      .single();

    if (error) {
      // console.error('Failed to create quote:', error);
      throw new Error(`Quote creation failed: ${error.message}`);
    }

    // console.log('Quote created successfully:', (data as Quote).id);
    return mapQuoteToData(data as Quote);
  }

  /**
   * Update an existing quote
   */
  async update(id: string, quoteData: Partial<QuoteData>): Promise<QuoteData> {
    const dbData = mapDataToQuote(quoteData) as QuoteUpdate;

    const { data, error } = await (supabase
      .from('quotes')
      .update(dbData as any)
      .eq('id', id)
      .select()
      .single() as any);

    if (error) {
      // console.error('Failed to update quote:', error);
      throw error;
    }

    return mapQuoteToData(data as Quote);
  }

  /**
   * Delete a quote
   */
  async delete(id: string): Promise<void> {
    const { error } = await supabase
      .from('quotes')
      .delete()
      .eq('id', id);

    if (error) {
      // console.error('Failed to delete quote:', error);
      throw error;
    }
  }

  /**
   * Update quote status
   */
  async updateStatus(id: string, status: QuoteStatus): Promise<QuoteData> {
    return this.update(id, { status });
  }

  /**
   * Mark quote as sent
   */
  async markAsSent(id: string): Promise<QuoteData> {
    return this.updateStatus(id, 'sent');
  }

  /**
   * Mark quote as accepted
   */
  async markAsAccepted(id: string): Promise<QuoteData> {
    return this.updateStatus(id, 'accepted');
  }

  /**
   * Mark quote as rejected
   */
  async markAsRejected(id: string): Promise<QuoteData> {
    return this.updateStatus(id, 'rejected');
  }

  /**
   * Generate next quote number
   */
  async generateQuoteNumber(): Promise<string> {
    const year = new Date().getFullYear();
    const prefix = `REL-${year}-`;

    try {
      // Get the latest quote with this year's prefix
      const { data, error } = await supabase
        .from('quotes')
        .select('quote_number')
        .like('quote_number', `${prefix}%`)
        .order('quote_number', { ascending: false })
        .limit(1);

      if (error) {
        // console.error('Error fetching latest quote number:', error);
        return `${prefix}0001`;
      }

      let nextNumber = 1;
      if (data && data.length > 0) {
        const lastQuoteNumber = (data[0] as { quote_number: string }).quote_number;
        const lastNumber = parseInt(lastQuoteNumber.split('-').pop() || '0', 10);
        nextNumber = lastNumber + 1;
      }

      return `${prefix}${String(nextNumber).padStart(4, '0')}`;
    } catch (error) {
      // console.error('Error generating quote number:', error);
      return `${prefix}0001`;
    }
  }
}

export const quotesApi = new QuotesApi();

// Quote status display labels
export const quoteStatusLabels: Record<QuoteStatus, string> = {
  draft: 'Draft',
  sent: 'Sent',
  accepted: 'Accepted',
  rejected: 'Rejected',
  expired: 'Expired',
};

// Quote status colors
export const quoteStatusColors: Record<QuoteStatus, string> = {
  draft: '#6B7280',
  sent: '#3B82F6',
  accepted: '#10B981',
  rejected: '#EF4444',
  expired: '#F59E0B',
};
